import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Network, Shield, Trash2, Check, X, Loader2 } from "lucide-react";

// Owner-only: grant a broker/admin access to another brokerage's clients, and
// approve/deny broker-initiated access requests.
export default function BrokerAccessManager({ client }) {
  const [grantees, setGrantees] = useState({ admins: [], brokers: [] });
  const [brokers, setBrokers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [grantee, setGrantee] = useState("");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gr, bk, gt] = await Promise.all([
        client.get("/admin/broker-access/grantees"),
        client.get("/admin/broker-access/brokers"),
        client.get("/admin/broker-access/grants"),
      ]);
      setGrantees(gr.data || { admins: [], brokers: [] });
      setBrokers(bk.data?.brokers || []);
      setGrants(gt.data?.grants || []);
    } catch (e) {
      setMsg("⚠ " + (e?.response?.data?.detail || "Failed to load permissions."));
    }
    setLoading(false);
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => grants.filter((g) => g.status === "active"), [grants]);
  const pending = useMemo(() => grants.filter((g) => g.status === "pending"), [grants]);
  const brokerById = useMemo(() => {
    const m = {};
    brokers.forEach((b) => { m[b.id] = b; });
    return m;
  }, [brokers]);
  const brokerMeta = (id) => {
    const b = brokerById[id];
    if (!b) return null;
    return `${b.email ? b.email + " · " : ""}${b.client_count ?? 0} client${(b.client_count ?? 0) === 1 ? "" : "s"}`;
  };

  const createGrant = async (grantee_user_id, target_broker_id) => {
    setBusy(true); setMsg("");
    try {
      await client.post("/admin/broker-access/grants", { grantee_user_id, target_broker_id });
      setMsg("✓ Access granted.");
      setGrantee(""); setTarget("");
      await load();
    } catch (e) {
      setMsg("⚠ " + (e?.response?.data?.detail || "Could not grant access."));
    }
    setBusy(false);
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this access link?")) return;
    setBusy(true); setMsg("");
    try {
      await client.delete(`/admin/broker-access/grants/${id}`);
      await load();
    } catch (e) {
      setMsg("⚠ " + (e?.response?.data?.detail || "Could not revoke."));
    }
    setBusy(false);
  };

  const revokeAll = async () => {
    if (!window.confirm("Revoke ALL broker access links? Every co-manager will be unlinked.")) return;
    setBusy(true); setMsg("");
    try {
      const r = await client.post("/admin/broker-access/revoke-all");
      setMsg(`✓ Removed ${r.data?.removed ?? 0} access link(s).`);
      await load();
    } catch (e) {
      setMsg("⚠ " + (e?.response?.data?.detail || "Could not revoke all."));
    }
    setBusy(false);
  };

  const revokeTarget = async (target_broker_id, name) => {
    if (!window.confirm(`Unlink every co-manager from "${name}"?`)) return;
    setBusy(true); setMsg("");
    try {
      const r = await client.post(`/admin/broker-access/revoke-target/${target_broker_id}`);
      setMsg(`✓ Unlinked ${r.data?.removed ?? 0} co-manager(s) from ${name}.`);
      await load();
    } catch (e) {
      setMsg("⚠ " + (e?.response?.data?.detail || "Could not unlink firm."));
    }
    setBusy(false);
  };

  // Firm-centric audit: group active grants by the brokerage being co-managed.
  const byFirm = useMemo(() => {
    const m = new Map();
    active.forEach((g) => {
      const key = g.target_broker_id;
      if (!m.has(key)) m.set(key, { id: key, name: g.target_name || key.slice(0, 8), managers: [] });
      m.get(key).managers.push(g);
    });
    return Array.from(m.values()).sort((a, b) => b.managers.length - a.managers.length);
  }, [active]);

  return (
    <section className="mb-12 scroll-mt-32" id="sec-permissions" data-testid="broker-access-manager">
      <div className="mb-5 flex items-center gap-3">
        <Network size={18} className="text-[#B8860B]" />
        <h2 className="text-2xl font-serif">Broker Access &amp; Permissions</h2>
      </div>
      <p className="text-[12.5px] text-[#8A8A8A] mb-6 max-w-2xl">
        Authorize an admin or a broker to <strong className="text-[#C9C9C0]">view and edit</strong> another brokerage's borrower clients.
        Only you (the owner) see everything; every other admin sees only their own brokerages plus the ones granted here.
      </p>

      {/* Grant form */}
      <div className="rounded-lg border border-[#2A2A22] bg-[#0F0F0A] p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={14} className="text-[#3DBB8E]" />
          <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#E4C97A]">Grant new access</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Grantee (who gets access)</span>
            <select value={grantee} onChange={(e) => setGrantee(e.target.value)} data-testid="bam-grantee-select"
              className="w-full bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]">
              <option value="">— Select admin or broker —</option>
              <optgroup label="Admins">
                {grantees.admins.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.email}</option>)}
              </optgroup>
              <optgroup label="Brokers">
                {grantees.brokers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </optgroup>
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Brokerage to unlock</span>
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="bam-target-select"
              className="w-full bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]">
              <option value="">— Select brokerage —</option>
              {brokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {(b.company || b.contact_name || b.id.slice(0, 8))}
                  {b.email ? ` · ${b.email}` : ""}
                  {` · ${b.client_count ?? 0} client${(b.client_count ?? 0) === 1 ? "" : "s"}`}
                </option>
              ))}
            </select>
            {target && (() => {
              const t = brokers.find((b) => b.id === target);
              if (t && (t.client_count ?? 0) === 0) return (
                <span className="block mt-1 text-[11px] text-[#E4A94A]" data-testid="bam-target-empty-warn">
                  ⚠ This brokerage has 0 clients — if you expected clients here, pick the duplicate entry that shows a client count.
                </span>
              );
              return null;
            })()}
          </label>
          <button
            disabled={busy || !grantee || !target}
            onClick={() => createGrant(grantee, target)}
            data-testid="bam-grant-btn"
            className="px-4 py-2 rounded-md bg-[#B8860B] text-[#0A0A0A] text-[13px] font-semibold hover:bg-[#d4a017] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Grant access
          </button>
        </div>
        {msg && <div className="mt-3 text-[12px] text-[#B7E4D0]" data-testid="bam-msg">{msg}</div>}
      </div>

      {/* Pending broker requests */}
      {pending.length > 0 && (
        <div className="rounded-lg border border-[#C9A227]/40 bg-[#1A1606] p-5 mb-6" data-testid="bam-pending">
          <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#E4C97A] mb-3">Pending broker requests</h3>
          <div className="space-y-2">
            {pending.map((g) => (
              <div key={g.id} className="flex items-center gap-3 text-[12.5px]" data-testid={`bam-pending-row-${g.id}`}>
                <span className="text-[#ECECE4]">{g.grantee_name}</span>
                <span className="text-[#5C5C5C]">wants to access</span>
                <span className="text-[#E4C97A]">{g.target_name}</span>
                <div className="ml-auto flex gap-2">
                  <button onClick={() => createGrant(g.grantee_user_id, g.target_broker_id)} disabled={busy}
                    data-testid={`bam-approve-${g.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#3DBB8E] text-[#0A0A0A] text-[11px] font-semibold hover:bg-[#5fd6ab]">
                    <Check size={12} /> Approve
                  </button>
                  <button onClick={() => revoke(g.id)} disabled={busy}
                    data-testid={`bam-deny-${g.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#FF8585]/50 text-[#FF8585] text-[11px] hover:bg-[#FF8585]/10">
                    <X size={12} /> Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active grants */}
      <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-5" data-testid="bam-active">
        <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#C9C9C0] mb-3">Active access links</h3>
        {loading ? (
          <div className="flex items-center gap-2 text-[#5C5C5C] text-[12px]"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : active.length === 0 ? (
          <div className="text-[12px] text-[#5C5C5C]" data-testid="bam-active-empty">No access links yet. Grant one above.</div>
        ) : (
          <div className="space-y-2">
            {active.map((g) => (
              <div key={g.id} className="flex items-center gap-3 text-[12.5px] py-1.5 border-b border-[#2A2A22] last:border-0" data-testid={`bam-active-row-${g.id}`}>
                <span className="text-[#ECECE4]">{g.grantee_name}</span>
                <span className="text-[#5C5C5C]">→ can view/edit</span>
                <span className="text-[#3DBB8E]">{g.target_name}</span>
                {brokerMeta(g.target_broker_id) && (
                  <span className="text-[10.5px] text-[#8A8A8A]" data-testid={`bam-active-meta-${g.id}`}>({brokerMeta(g.target_broker_id)})</span>
                )}
                <button onClick={() => revoke(g.id)} disabled={busy}
                  data-testid={`bam-revoke-${g.id}`}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#FF8585]/40 text-[#FF8585] text-[11px] hover:bg-[#FF8585]/10">
                  <Trash2 size={12} /> Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Access Audit — firm-centric co-management view */}
      <div className="rounded-lg border border-[#2A2A22] bg-[#0F0F0A] p-5 mt-6" data-testid="bam-audit">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Network size={14} className="text-[#B8860B]" />
          <h3 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#E4C97A]">Access Audit</h3>
          <span className="text-[11px] text-[#5C5C5C]">— who co-manages which brokerage</span>
          {active.length > 0 && (
            <button onClick={revokeAll} disabled={busy} data-testid="bam-revoke-all"
              className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#FF8585]/50 text-[#FF8585] text-[11px] font-semibold hover:bg-[#FF8585]/10 disabled:opacity-40">
              <Trash2 size={12} /> Revoke ALL links
            </button>
          )}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-[#5C5C5C] text-[12px] mt-3"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : byFirm.length === 0 ? (
          <div className="text-[12px] text-[#5C5C5C] mt-3" data-testid="bam-audit-empty">No brokerage is currently co-managed. Grants you create appear here grouped by firm.</div>
        ) : (
          <div className="space-y-3 mt-3">
            {byFirm.map((f) => (
              <div key={f.id} className="rounded-md border border-[#2A2A22] bg-[#13130E] p-3.5" data-testid={`bam-audit-firm-${f.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[13px] font-semibold text-[#3DBB8E]">{f.name}</span>
                  {brokerMeta(f.id) && (
                    <span className="text-[10.5px] text-[#8A8A8A]" data-testid={`bam-audit-meta-${f.id}`}>({brokerMeta(f.id)})</span>
                  )}
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] bg-[#1A1606] border border-[#3a2f12] rounded-full px-2 py-0.5" data-testid={`bam-audit-count-${f.id}`}>
                    {f.managers.length} co-manager{f.managers.length === 1 ? "" : "s"}
                  </span>
                  <button onClick={() => revokeTarget(f.id, f.name)} disabled={busy} data-testid={`bam-audit-revoke-firm-${f.id}`}
                    className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#FF8585]/40 text-[#FF8585] text-[11px] hover:bg-[#FF8585]/10">
                    <Trash2 size={12} /> Unlink all
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {f.managers.map((g) => (
                    <span key={g.id} className="inline-flex items-center gap-1.5 text-[11.5px] text-[#ECECE4] bg-[#0F0F0A] border border-[#2A2A22] rounded-full pl-2.5 pr-1 py-0.5" data-testid={`bam-audit-mgr-${g.id}`}>
                      {g.grantee_name}
                      <button onClick={() => revoke(g.id)} disabled={busy} title="Revoke this co-manager"
                        data-testid={`bam-audit-mgr-revoke-${g.id}`}
                        className="text-[#FF8585] hover:bg-[#FF8585]/15 rounded-full w-4 h-4 inline-flex items-center justify-center">
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
